import React, { Fragment } from 'react';
import styled from 'styled-components';

import { Button } from 'components/core';
import { HorizontalDivider } from 'components/core/divider';
import { DesktopOnly, SmallLoader } from 'shared/components';
import EmptyLoadingState from 'components/empty-loading-state';
import { OrdersErrorState } from 'components/empty-loading-state/empty-page-states/orders-error-state';
import { ARIA_ROLES } from 'shared/constants';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useReorder } from 'src/hooks/use-reorder';
import { ListContainer, TabHeaderTitle } from '../../components';
import { OrderListItem } from './order-list-item';
import { UseOrdersForConsumerReturn } from './use-orders-for-consumer/use-orders-for-consumer';
import { ConsumerOrder } from '../../account.types';
import { OrderSource } from './order-list-tab-bar';

export type OrderListProps = Pick<UseOrdersForConsumerReturn, 'loading' | 'orders'> & {
  handleLoadMore: () => void;
  hasMoreOrders: boolean;
  viewableStatuses: Set<string>;
  showLoadingSpinner: boolean;
  handleViewOrder: (order: ConsumerOrder, canViewStatus: boolean) => void;
  hideHeader?: boolean;
  orderSource?: OrderSource;
  hasError: boolean;
  onRefetch: () => void;
};

export function OrderList(props: OrderListProps): JSX.Element {
  const { dispensary } = useDispensary();
  const currentDispensaryId = dispensary?.id;
  const { reorder, loadingOrderId } = useReorder();
  const {
    handleLoadMore,
    hasMoreOrders,
    loading,
    orders,
    viewableStatuses,
    showLoadingSpinner,
    handleViewOrder,
    hideHeader = false,
    orderSource,
    hasError,
    onRefetch,
  } = props;

  return (
    <OrdersContainer>
      {!hideHeader && (
        <DesktopOnly>
          <TabHeaderTitle tag='h2' size='medium'>
            Orders
          </TabHeaderTitle>
        </DesktopOnly>
      )}

      <OrdersListContainer>
        {!hasError &&
          orders.map((order: ConsumerOrder, index) => {
            const canViewStatus = typeof order.status === 'string' && viewableStatuses.has(order.status);
            const enabledReorder = orderSource !== OrderSource.inStore && !!reorder;
            const isFromDifferentLocation =
              !!currentDispensaryId && !!order.dispensary?._id && order.dispensary._id !== currentDispensaryId;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={`${order.orderId}-${index}`}>
                <OrderListItem
                  order={order}
                  canViewStatus={canViewStatus}
                  onViewOrder={() => handleViewOrder(order, canViewStatus)}
                  onReorder={enabledReorder ? () => void reorder(order, { source: 'order list' }) : undefined}
                  reorderLoading={loadingOrderId === order.orderId}
                  orderSource={orderSource}
                  isFromDifferentLocation={enabledReorder && isFromDifferentLocation}
                />
                {index < orders.length - 1 && <OrdersListDivider mt={24} mb={25} />}
              </Fragment>
            );
          })}

        {!hasError &&
          (showLoadingSpinner ? (
            <SmallLoader />
          ) : (
            <LoadMoreButton
              variant='secondary'
              role={ARIA_ROLES.BUTTON}
              aria-label='Load More'
              onClick={handleLoadMore}
              show={hasMoreOrders}
            >
              Load More
            </LoadMoreButton>
          ))}

        {hasError && <OrdersErrorState onRefetch={onRefetch} loading={loading} />}
        {!hasError && !loading && orders.length === 0 && <EmptyLoadingState page='orders' isEmpty />}
      </OrdersListContainer>
    </OrdersContainer>
  );
}

const LoadMoreButton = styled(Button)<{ show?: boolean }>`
  margin-top: 35px;
  display: ${({ show }) => (show ? `flex` : `none`)};
  justify-content: center;
  align-items: center;
  border: 2px solid var(--dcm-color-primary);
  color: var(--dcm-color-primary);
  width: 99px;
  height: 29px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: auto;
    margin-top: 24px;
  }
`;

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const OrdersListContainer = styled(ListContainer)`
  margin-top: 10px;
`;

const OrdersListDivider = styled(HorizontalDivider)`
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: flex;
  }
`;
